import { SALE_TRANSACTION_FINISHED } from "../../../_Bootstrap/GlobalConstants";
import NotFoundException from "../../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import Cloner from "../../../Utilities/Generic/Cloner";
import { getLogLevel, LogLevel } from "../../../Utilities/Platform/Logging";
import { isInIndexingContext, isInPromotionWorkerContext } from "../../../Utilities/Platform/WorkerHelpers";
import { SearchCommunicator } from "../../../Workers/Search/SearchCommunicator";
import { SearchIndexes } from "../../../Workers/Search/SearchEvents";
import { type BasePromotionRepository } from "../../_Bases/BasePromotionRepository";
import { type LocalDatabasePromotion } from "../../_Bases/BasePromotionRepository";
import { IndexedDBDataSource, type InternalIndexedDBDatabase } from "../../DataStores/Database";
import { determineDataSource } from "../../DataStores/DataSource";
import BaseIndexedDBRepository from "../../Implementations/BaseIndexedDBRepository";

type PromotionMappingKey = "products" | "brands" | "categories" | "families" | "tags";

export default class IndexedDBPromotionRepository extends BaseIndexedDBRepository<
    LocalDatabasePromotion
> implements BasePromotionRepository {
    public readonly source: InternalIndexedDBDatabase["promotions"];
    protected readonly dataTypeName = "Promotion";

    protected onReMap: () => void;
    protected currentlyMapping: boolean;
    protected initialMappingTimer: ReturnType<typeof setTimeout> | undefined;
    protected mappingResumeTimer: ReturnType<typeof setTimeout> | undefined;
    protected readonly mappingSmoothingTime = isInPromotionWorkerContext() ? 1000 : 5000; // 1 second or 5 seconds
    protected reindexSmoothingTimeout: ReturnType<typeof setTimeout> | undefined;
    protected hasIndexed = false;
    protected onFirstMapping: (() => void);

    protected simulatedPromotion: {
        originalId: null | string;
        promotion: null | LocalDatabasePromotion;
    } = {
        originalId: null,
        promotion : null,
    };

    protected promotionCache: Record<string, LocalDatabasePromotion>;

    protected promotionProductCache: Record<string, Array<string>>;

    protected promotionMapping: {
        products: Record<string, Array<string>>;
        brands: Record<string, Array<string>>;
        categories: Record<string, Array<string>>;
        families: Record<string, Array<string>>;
        tags: Record<string, Array<string>>;
        all: Array<string>;
    };

    constructor(source: InternalIndexedDBDatabase) {
        super();

        this.source = source.promotions;

        this.resetMap();

        this.onReMap = () => { /* no-op */ };
        this.onFirstMapping = () => { /* no-op */ };
        this.currentlyMapping = false;
        this.mappingResumeTimer = undefined;
        this.promotionCache = {};
        this.promotionProductCache = {};

        this.mapToCache = this.mapToCache.bind(this);
        this.resetPromotionCache = this.resetPromotionCache.bind(this);

        // Create the promotion mapping
        this.initialMappingTimer = setTimeout(() => {
            void this.mapToCache();
        }, 2000);

        // Listen for sync events
        if(typeof document !== "undefined") {
            document.addEventListener("synchronisationFinished", (event: CustomEvent) => {
                if(!event.detail.updated?.promotions) {
                    return;
                }

                void this.mapToCache();
            });
        }

        // Listen for transaction end events
        if(typeof document !== "undefined") {
            document.addEventListener(SALE_TRANSACTION_FINISHED, this.resetPromotionCache);
        }
    }

    protected resetMap(): void {
        this.resetPromotionCache();
        this.promotionMapping = {
            products  : {},
            brands    : {},
            categories: {},
            families  : {},
            tags      : {},
            all       : [],
        };
    }

    protected resetPromotionCache(): void {
        this.promotionCache = {};
        this.promotionProductCache = {};
    }

    public async mapToCache(): Promise<void> {
        if(typeof this.mappingResumeTimer !== "undefined") {
            return;
        }

        if(this.currentlyMapping) {
            if(typeof this.onReMap === "undefined") {
                this.onReMap = this.mapToCache;
            }

            return;
        }

        if(typeof this.reindexSmoothingTimeout !== "undefined") {
            return;
        } else if(typeof this.initialMappingTimer === "undefined") {
            await new Promise<void>(res => {
                this.reindexSmoothingTimeout = setTimeout(() => {
                    res();
                }, this.mappingSmoothingTime);
            });
        } else {
            clearTimeout(this.initialMappingTimer);
            this.initialMappingTimer = undefined;
        }

        if(!isInIndexingContext()) {
            this.currentlyMapping = false;
            this.initialMappingTimer = undefined;
            this.reindexSmoothingTimeout = undefined;

            return;
        }

        if(getLogLevel() === LogLevel.VERBOSE) {
            console.debug("Remapping promotions");
        }

        this.currentlyMapping = true;

        const startTime = performance.now();

        this.resetMap();

        const indexPromotion = (promotion: LocalDatabasePromotion) => {
            for(let j = 0, n = promotion.criteria.length; j < n; j++) {
                const criteria = promotion.criteria[j];

                if(typeof criteria.allItems === "number") {
                    this.promotionMapping.all.push(promotion.uuid);
                }

                for(let k = 0, c = fields.length; k < c; k++) {
                    const field = fields[k];

                    for(let index = 0, length = criteria[field].length; index < length; index++) {
                        const id = criteria[field][index].uuid;

                        if(typeof this.promotionMapping[field][id] === "undefined") {
                            this.promotionMapping[field][id] = [];
                        }

                        this.promotionMapping[field][id].push(promotion.uuid);
                    }
                }
            }
        };

        const fields: Array<PromotionMappingKey> = [ "brands", "categories", "families", "products", "tags" ];

        await IndexedDBDataSource.each(this.source, promotion => {
            if(this.simulatedPromotion.originalId) {
                if(promotion.uuid === this.simulatedPromotion.originalId) {
                    return;
                }
            }

            indexPromotion(promotion);
        });

        if(this.simulatedPromotion.promotion) {
            if(getLogLevel() === LogLevel.VERBOSE) {
                console.log("Simulated promotion added to reindex");
            }

            indexPromotion(this.simulatedPromotion.promotion);
        }

        const endTime = performance.now();

        if(getLogLevel() === LogLevel.VERBOSE) {
            console.debug(`Finished indexing promotions. Time taken was ${(endTime - startTime) / 1000} seconds`);
        }

        this.currentlyMapping = false;

        if(!this.hasIndexed) {
            this.hasIndexed = true;

            if(typeof this.onFirstMapping === "function") {
                this.onFirstMapping();
                this.onFirstMapping = () => { /* no-op */ };
            }
        }

        this.initialMappingTimer = undefined;
        this.reindexSmoothingTimeout = undefined;

        if(typeof this.onReMap === "function") {
            this.onReMap();
            this.onReMap = () => { /* no-op */ };
        }
    }

    public async search(value: string): Promise<Array<LocalDatabasePromotion>> {
        const ids = await SearchCommunicator.search(SearchIndexes.PROMOTION_NAME, value.trim());

        if(!ids.length) {
            return [];
        }

        return this.source
            .where("uuid")
            .anyOf(ids)
            .sortBy("name");
    }

    public async get(uuid: string): Promise<LocalDatabasePromotion> {
        if(uuid === "simulate") {
            if(this.simulatedPromotion.promotion === null) {
                throw new NotFoundException("Simulated promotion was not found");
            }

            return Cloner.Clone(this.simulatedPromotion.promotion);
        } else if(uuid === this.simulatedPromotion.originalId) {
            throw new NotFoundException("Promotion is currently being simulated, but not called with simulate");
        } else if(typeof this.promotionCache[uuid] !== "undefined") {
            return Cloner.Clone(this.promotionCache[uuid]);
        }

        return this.source
            .get(uuid)
            .then(promotion => {
                if(!promotion) {
                    throw new NotFoundException(`Promotion Not Found (${uuid})`);
                }

                this.promotionCache[uuid] = promotion;

                return promotion;
            });
    }

    public async getBulk(ids: Array<string>): Promise<Array<LocalDatabasePromotion>> {
        const append: Array<LocalDatabasePromotion> = [];

        if(ids.includes("simulate") && this.simulatedPromotion.promotion !== null) {
            append.push(Cloner.Clone(this.simulatedPromotion.promotion));
        }

        return this.source.where("uuid").anyOf(ids).toArray()
            .then(promotions => ([
                ...promotions,
                ...append,
            ]));
    }

    public async getPromotionsForProduct(product: string): Promise<Array<string>> {
        if(typeof this.promotionProductCache[product] !== "undefined") {
            return this.promotionProductCache[product];
        }

        const source = await determineDataSource();
        const dbProduct = await source.products.get(product);

        if(!this.hasIndexed) {
            await new Promise<void>(res => {
                const cb = this.onFirstMapping;

                this.onFirstMapping = () => {
                    cb();
                    res();
                };
            });
        }

        if(this.currentlyMapping) {
            await new Promise<void>(res => {
                const cb = this.onReMap;

                this.onFirstMapping = () => {
                    cb();
                    res();
                };
            });
        }

        const promotions = [];

        if(typeof this.promotionMapping.products[product] !== "undefined") {
            promotions.push(...this.promotionMapping.products[product]);
        }

        if(dbProduct.brand_id) {
            if(typeof this.promotionMapping.brands[dbProduct.brand_id] !== "undefined") {
                promotions.push(...this.promotionMapping.brands[dbProduct.brand_id]);
            }
        }

        if(dbProduct.category_id) {
            if(typeof this.promotionMapping.categories[dbProduct.category_id] !== "undefined") {
                promotions.push(...this.promotionMapping.categories[dbProduct.category_id]);
            }
        }

        if(dbProduct.family_id) {
            if(typeof this.promotionMapping.families[dbProduct.family_id] !== "undefined") {
                promotions.push(...this.promotionMapping.families[dbProduct.family_id]);
            }
        }

        for(let i = 0, l = dbProduct.tags.length; i < l; i++) {
            if(typeof this.promotionMapping.tags[dbProduct.tags[i]] !== "undefined") {
                promotions.push(...this.promotionMapping.tags[dbProduct.tags[i]]);
            }
        }

        promotions.push(...this.promotionMapping.all);

        this.promotionProductCache[product] = promotions;

        return promotions;
    }

    public getByCategory(id: string): Promise<Array<LocalDatabasePromotion>> {
        return this.source.where("promotion_category_id").equals(id).toArray();
    }

    public filter(filter: (promotion: LocalDatabasePromotion) => boolean): Promise<Array<LocalDatabasePromotion>> {
        return this.source.filter(filter).toArray();
    }

    public simulate(
        originalId: null | string,
        promotion: null | LocalDatabasePromotion
    ): Promise<void> {
        if(getLogLevel() === LogLevel.VERBOSE) {
            if(promotion === null) {
                console.log("Unloading simulated promotion");
            } else {
                console.log("Loading simulated promotion");
            }
        }

        // Reset the caches
        this.resetMap();

        // Store the simulated promotion
        this.simulatedPromotion = {
            originalId,
            promotion,
        };

        // Ensure the reindex doesn't smooth
        this.initialMappingTimer = setTimeout(() => {
            // Do nothing, just has to be a timeout
        }, 100);

        // Reindex the promotions
        return this.mapToCache();
    }

    private replaceIndexes(
        id: string,
        name?: string
    ): void {
        void SearchCommunicator.updateIndex(SearchIndexes.PROMOTION_NAME, id, name);
    }

    private clearIndexes(): void {
        void SearchCommunicator.emptyIndex(SearchIndexes.PROMOTION_NAME);
    }

    public put(item: Array<LocalDatabasePromotion> | LocalDatabasePromotion): Promise<string> {
        return super.put(item)
            .then(async id => {
                if(!Array.isArray(item)) {
                    item = [ item ];
                }

                for(let i = 0, l = item.length; i < l; i++) {
                    this.replaceIndexes(
                        item[i].uuid,
                        item[i].name
                    );
                }

                return id;
            });
    }

    public delete(item: Array<string> | string): Promise<void> {
        return super.delete(item)
            .then(() => {
                if(!Array.isArray(item)) {
                    item = [ item ];
                }

                for(let i = 0, l = item.length; i < l; i++) {
                    this.replaceIndexes(item[i]);
                }
            });
    }

    public empty(): Promise<void> {
        return super.empty()
            .then(() => {
                this.clearIndexes();
            });
    }
}
